<template>
  <div
    :class="[cardMinHeight, { 'pricing-card--current': isCurrent }]"
    class="pricing-card group flex h-full w-full flex-col rounded-xl border bg-white shadow transition duration-100 hover:scale-[1.02] lg:flex-1"
  >
    <div class="flex h-full grow flex-col p-6 lg:p-12">
      <div class="grow">
        <!-- Icon -->
        <div>
          <component
            :is="iconComponent"
            filled
            :font-controlled="false"
            class="h-9 w-9"
          />
        </div>

        <!-- Title -->
        <h2 class="font-title-6 mt-3">{{ title }}</h2>

        <!-- Description -->
        <p class="font-body text-color-tertiary mt-2">{{ description }}</p>

        <!-- Pricing Section (hidden for enterprise) -->
        <div v-if="!isEnterprise" class="mt-8">
          <h3 class="font-title-4">
            {{ amount }}
          </h3>
          <p class="font-subheadline text-color-tertiary mt-2">
            {{ subtitle }}
          </p>
        </div>

        <hr class="my-6 text-[#E6E6EA]" />

        <!-- Features Section - Smart Rendering -->
        <div class="flex-grow">
          <div
            v-if="typeof features === 'string'"
            class="font-body text-color-tertiary"
          >
            {{ features }}
          </div>
          <ul v-else class="font-subheadline text-color-tertiary space-y-3">
            <li
              v-for="(feature, index) in features"
              :key="index"
              class="flex items-center"
            >
              <SvgoCheckCircle filled class="mr-2 shrink-0" />
              <span>{{ feature }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Action Button -->
      <div v-if="showAction" class="mt-7 w-full">
        <hr v-if="!isEnterprise" class="mb-6 text-[#E6E6EA]" />
        <AppButton
          :label="buttonText"
          class="pricing-card-action-btn w-full"
          :loading="buttonLoading"
          :disabled="buttonDisabled"
          @click="handleClick"
        ></AppButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import {
  type PriceProductKey as PriceProductKeyType,
  PriceProductKey,
} from '~/utils/enums/Price';
import AppButton from '../base/AppButton.vue';

// Define props with TypeScript
const { type, showAction, interval, priceSubtitle, isCurrent } = defineProps({
  type: {
    type: String as PropType<PriceProductKeyType>,
    required: false,
    default: PriceProductKey.INDIVIDUAL,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  amount: {
    type: String,
    default: '',
  },
  priceSubtitle: {
    type: String as PropType<string | null | undefined>,
    default: null,
  },
  interval: {
    type: String,
    default: '',
  },
  features: {
    type: [String, Array] as PropType<string | string[]>,
    required: true,
  },
  buttonText: {
    type: String,
    default: 'Choose',
  },
  buttonLoading: {
    type: Boolean,
    default: false,
  },
  buttonDisabled: {
    type: Boolean,
    default: false,
  },
  showAction: {
    type: Boolean,
    default: true,
  },
  isCurrent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selected']);

const subtitle = computed(() => priceSubtitle ?? `Per ${interval}`);
const isEnterprise = computed(() => type === PriceProductKey.ENTERPRISE);

const iconComponent = computed(() => {
  switch (type) {
    case PriceProductKey.COMPANY:
      return 'SvgoCompany';
    case PriceProductKey.ENTERPRISE:
      return 'SvgoEnterprise';
    default:
      return 'SvgoIndividual';
  }
});

const cardMinHeight = computed(() =>
  showAction ? 'min-h-[552px]' : 'min-h-[456px]',
);

// Handle click event
const handleClick = () => {
  emit('selected');
};
</script>

<style scoped lang="scss">
.pricing-card {
  &--current {
    border-color: $border-color-brand;
    box-shadow: 0 0 0 2px $border-color-brand;
  }
}

.pricing-card-action-btn {
  &:disabled {
    background: #f2f2f4 !important;
    color: #b4b4bf !important;
  }
}
</style>
